/**
 * Client-side image compression before upload (canvas resize → JPEG).
 * Videos and non-image files pass through unchanged.
 */

export const DEFAULT_MAX_EDGE = 1920;
export const DEFAULT_JPEG_QUALITY = 0.8;

/**
 * @param {File|Blob} file
 * @param {{ maxEdge?: number, quality?: number }} [options]
 * @returns {Promise<File|Blob>}
 */
export async function compressImageForUpload(file, options = {}) {
  if (!file || typeof file !== 'object') return file;
  const type = file.type || '';
  if (!type.startsWith('image/')) return file;
  // Keep animated GIFs intact.
  if (type === 'image/gif') return file;

  const maxEdge = options.maxEdge ?? DEFAULT_MAX_EDGE;
  const quality = options.quality ?? DEFAULT_JPEG_QUALITY;

  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImage(objectUrl);
    const { width, height } = img;
    if (!width || !height) return file;

    const longest = Math.max(width, height);
    const scale = longest > maxEdge ? maxEdge / longest : 1;
    const targetW = Math.max(1, Math.round(width * scale));
    const targetH = Math.max(1, Math.round(height * scale));

    // Skip re-encode when already small enough and already JPEG.
    if (scale === 1 && type === 'image/jpeg' && file.size < 1.5 * 1024 * 1024) {
      return file;
    }

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, targetW, targetH);

    const blob = await canvasToJpegBlob(canvas, quality);
    if (!blob) return file;

    const baseName = typeof file.name === 'string' && file.name
      ? file.name.replace(/\.[^.]+$/, '')
      : 'image';
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });
}

function canvasToJpegBlob(canvas, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
  });
}
